import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

import { IMAGE_GENERATION_COST } from '../../utils/const';
import { colors } from '../../utils/colors';
import { GradientButton } from '../minicomponents/GradientButton';
import { IconClose, IconWarning, IconGenerateImage } from '../minicomponents/icons';

interface ImageGenerateConfirmPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  creditsLeft: number;
  isGenerating?: boolean;
}

const ImageGenerateConfirmPopup: React.FC<ImageGenerateConfirmPopupProps> = ({
  open,
  onClose,
  onConfirm,
  creditsLeft,
  isGenerating = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleConfirm = () => {
    onConfirm();
  };

  const navigateToSubscriptionPage = () => {
    navigate('/app/profile/subscription');
    onClose();
  };

  const hasEnoughCredits = creditsLeft >= IMAGE_GENERATION_COST;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        component: motion.div,
        initial: { scale: 0.9, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 0.9, opacity: 0 },
        transition: { type: 'spring', stiffness: 300, damping: 30 },
        sx: {
          maxWidth: '500px',
          width: 'auto',
          maxHeight: 'calc(100vh - 104px)',
        },
      }}
    >
      <Grid container size={12} sx={{ p: 3, pb: 2, alignItems: 'center' }}>
        <Grid size={'grow'}>
          <DialogTitle sx={{ p: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconGenerateImage />
              <Typography variant="h5">
                {t('app.structuredItems.actions.generateImage.confirmTitle')}
              </Typography>
            </Stack>
          </DialogTitle>
        </Grid>
        <Grid size={'auto'} justifyContent="flex-end">
          <IconButton onClick={onClose} disabled={isGenerating}>
            <IconClose />
          </IconButton>
        </Grid>
      </Grid>

      <DialogContent>
        {hasEnoughCredits ? (
          <Stack spacing={2}>
            <Typography variant="body1">
              {t('app.structuredItems.actions.generateImage.confirmMessage')}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                p: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(25, 118, 210, 0.2)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {t('app.structuredItems.actions.generateImage.creditCost', {
                  cost: IMAGE_GENERATION_COST,
                  remaining: creditsLeft - IMAGE_GENERATION_COST,
                })}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconWarning sx={{ color: 'warning.main' }} />
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {t('app.popups.generate.notEnoughCredits')}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {t('app.structuredItems.actions.generateImage.upgradeMessage', {
                cost: IMAGE_GENERATION_COST,
                available: creditsLeft,
              })}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {hasEnoughCredits ? (
          <Stack direction="row" spacing={2}>
            <GradientButton
              buttonText={t('app.structuredItems.actions.generateImage.confirmButton')}
              onSubmitButtonHandler={handleConfirm}
              disabled={isGenerating}
              iconLeft={<IconGenerateImage />}
            />
            <Button onClick={onClose} disabled={isGenerating}>
              {t('app.buttons.cancel')}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2}>
            <GradientButton
              buttonText={t('website.upgradePopup.buttonUpgrade')}
              onSubmitButtonHandler={navigateToSubscriptionPage}
              disabled={isGenerating}
            />
            <Button onClick={onClose} disabled={isGenerating}>
              {t('app.buttons.cancel')}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageGenerateConfirmPopup;
